import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, finalize, tap } from 'rxjs/operators';
import { Confirmation } from '@abp/ng.theme.shared';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { FilterService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { CxMultiSelectComponent } from 'src/app/shared/components/cx-multi-select/cx-multi-select.component';
import { CxMultiSelectLazyLoadEvent } from 'src/app/shared/components/cx-multi-select/models/cx-multi-select-lazy-load-event.model';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { CrmSegmentContactDto } from '../../models/crm-segment-contact.dto';
import { CrmSegmentDefinitionDto } from '../../models/crm-segment-definition.dto';
import { PagedContactsState } from '../../models/paged-contacts-state.model';
import { SegmentContactRow } from '../../models/segment-contact-row.model';
import { CrmSegmentDefinitionService } from '../../services/crm-segment-definition.service';
import { toSegmentContactRow } from '../../utils/segment-contact-row.util';

/** Contacts fetched per page, for both the picker and the "Already assigned" section. */
const CONTACTS_PAGE_SIZE = 50;

/** Delay after the last keystroke before the picker's search goes to the server. */
const SEARCH_DEBOUNCE_MS = 300;

/** How close to the end of the loaded options (picker) or the list bottom in pixels (assigned) the next page loads. */
const PICKER_LOAD_MORE_THRESHOLD = 10;
const ASSIGNED_LOAD_MORE_THRESHOLD_PX = 40;

/**
 * PrimeNG filter match mode that matches every option: the picker's options already are the server's search
 * results, and filtering them again in the browser would drop the ones matched on a field it does not show.
 */
const SERVER_SIDE_FILTER_MODE = 'cxServerSide';

@Component({
  selector: 'app-crm-segment-manage-contacts-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, LocalizationPipe, DialogModule, CxMultiSelectComponent, FormActionsComponent],
  templateUrl: './crm-segment-manage-contacts-dialog.component.html',
})
export class CrmSegmentManageContactsDialogComponent implements OnChanges {
  private readonly crmSegmentDefinitionService = inject(CrmSegmentDefinitionService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toasterService = inject(ToasterService);
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @Input() visible = false;
  @Input() segment: CrmSegmentDefinitionDto | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  readonly serverSideFilterMode = SERVER_SIDE_FILTER_MODE;

  /** Loaded assigned contacts still kept (the ones removed in this dialog are taken out). */
  alreadyAssigned: SegmentContactRow[] = [];
  newContacts: SegmentContactRow[] = [];
  /** The current page(s) of the picker's search, without the contacts already staged as new. */
  availableContactOptions: SegmentContactRow[] = [];
  selectedContactIds: number[] = [];
  isAssignedExpanded = false;
  loading = false;
  saving = false;

  /** Assigned contacts removed one by one; ignored once Unassign All is confirmed. */
  private removedAssignedIds = new Set<number>();
  private removeAll = false;
  /** Every contact seen in the picker, so a selection survives a new search that no longer lists it. */
  private knownContacts = new Map<number, SegmentContactRow>();
  private searchTerm = '';
  private readonly searchTerms = new Subject<string>();

  private assignedState: PagedContactsState = this.emptyPagedState();
  private pickerState: PagedContactsState = this.emptyPagedState();

  constructor() {
    inject(FilterService).register(SERVER_SIDE_FILTER_MODE, () => true);

    this.searchTerms
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        // Compared with the live term rather than distinctUntilChanged, which would remember a term across
        // dialog openings and swallow the same search typed again after a reopen.
        filter(term => term !== this.searchTerm),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(term => {
        this.searchTerm = term;
        this.reloadPicker();
      });
  }

  /** Number of contacts that stay assigned after the pending changes. */
  get assignedCount(): number {
    if (this.removeAll) {
      return 0;
    }
    return Math.max((this.assignedState.total ?? 0) - this.removedAssignedIds.size, 0);
  }

  get assignedLoadingMore(): boolean {
    return this.assignedState.loading && this.assignedState.fetched > 0;
  }

  get pickerLoading(): boolean {
    return this.pickerState.loading;
  }

  get canSave(): boolean {
    return this.newContacts.length > 0 || this.removedAssignedIds.size > 0 || this.removeAll;
  }

  get contactsSelectedLabel(): string {
    return `{0} ${this.localization.instant('Settings::CRM:Definitions:ContactsSelected')}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.segment) {
      this.resetState();
      this.loadInitialPages();
    }
  }

  onSearch(term: string): void {
    this.searchTerms.next(term.trim());
  }

  onPickerLazyLoad(event: CxMultiSelectLazyLoadEvent): void {
    if (event.last >= this.availableContactOptions.length - PICKER_LOAD_MORE_THRESHOLD) {
      this.loadNextPickerPage();
    }
  }

  onAssignedScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < ASSIGNED_LOAD_MORE_THRESHOLD_PX) {
      this.loadNextAssignedPage();
    }
  }

  onAdd(): void {
    if (!this.selectedContactIds.length) return;

    const toAdd = this.selectedContactIds
      .map(id => this.knownContacts.get(id))
      .filter((row): row is SegmentContactRow => !!row);

    this.newContacts = [...this.newContacts, ...toAdd];
    this.selectedContactIds = [];

    const newIds = new Set(this.newContacts.map(c => c.id));
    this.availableContactOptions = this.availableContactOptions.filter(c => !newIds.has(c.id));
    this.cdr.markForCheck();
  }

  onRemoveAssigned(index: number): void {
    const row = this.alreadyAssigned[index];
    if (!row) return;

    this.removedAssignedIds.add(row.id);
    this.alreadyAssigned = this.alreadyAssigned.filter((_, i) => i !== index);
    this.cdr.markForCheck();

    // Removing rows can shrink the list below its scroll height, after which no scroll event would ever ask
    // for the rest of the segment; top it up instead.
    if (this.alreadyAssigned.length < CONTACTS_PAGE_SIZE / 2) {
      this.loadNextAssignedPage();
    }
  }

  onRemoveNew(index: number): void {
    this.newContacts = this.newContacts.filter((_, i) => i !== index);
    // Re-run the current search so the contact becomes selectable again where it belongs in the order.
    this.reloadPicker();
  }

  onUnassignAll(): void {
    if (!this.segment) return;

    const message = this.localization.instant(
      'Settings::CRM:Definitions:UnassignAllConfirmMessage',
      this.segment.name,
    );

    this.confirmationService
      .warn(message, 'Settings::CRM:Definitions:UnassignAllConfirmTitle', {
        hideCancelBtn: false,
        hideYesBtn: false,
        dismissible: true,
        yesText: 'Settings::CRM:Definitions:UnassignAll',
        cancelText: 'AbpUi::Cancel',
      })
      .subscribe(status => {
        if (status !== Confirmation.Status.confirm) return;

        // Staged like the row-level removals and sent as one flag on Save, so the assigned contacts never
        // have to be loaded to be removed.
        this.removeAll = true;
        this.removedAssignedIds.clear();
        this.alreadyAssigned = [];
        this.isAssignedExpanded = false;
        this.cdr.markForCheck();
      });
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onSave(): void {
    if (!this.segment || !this.canSave || this.saving) return;

    this.saving = true;
    this.crmSegmentDefinitionService
      .updateContacts(this.segment.id, {
        addedContactIds: this.newContacts.map(c => c.id),
        removedContactIds: this.removeAll ? [] : [...this.removedAssignedIds],
        removeAll: this.removeAll,
      })
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.toasterService.success('Settings::CRM:Definitions:ManageContactsSaveSuccess');
          this.saved.emit();
          this.visibleChange.emit(false);
        },
      });
  }

  trackById(_: number, row: SegmentContactRow): number {
    return row.id;
  }

  private loadInitialPages(): void {
    const segmentId = this.segment?.id;
    if (!segmentId) {
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.fetchPage(this.assignedState, () =>
      this.crmSegmentDefinitionService.getAssignedContactPage(segmentId, 0, CONTACTS_PAGE_SIZE),
    )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: response => this.appendAssigned(response),
        error: () => {
          this.assignedState.total = 0;
        },
      });

    this.reloadPicker();
  }

  private loadNextAssignedPage(): void {
    const segmentId = this.segment?.id;
    const state = this.assignedState;
    if (!segmentId || this.removeAll || state.loading || state.total === null || state.fetched >= state.total) {
      return;
    }

    this.fetchPage(state, () =>
      this.crmSegmentDefinitionService.getAssignedContactPage(segmentId, state.fetched, CONTACTS_PAGE_SIZE),
    ).subscribe({ next: response => this.appendAssigned(response) });
  }

  private reloadPicker(): void {
    this.pickerState = this.emptyPagedState(this.pickerState.requestId);
    this.availableContactOptions = [];
    this.loadNextPickerPage();
  }

  private loadNextPickerPage(): void {
    const segmentId = this.segment?.id;
    const state = this.pickerState;
    if (!segmentId || state.loading || (state.total !== null && state.fetched >= state.total)) {
      return;
    }

    const term = this.searchTerm;
    this.fetchPage(state, () =>
      this.crmSegmentDefinitionService.getAssignableContacts(segmentId, term, state.fetched, CONTACTS_PAGE_SIZE),
    ).subscribe({
      next: response => {
        const newIds = new Set(this.newContacts.map(c => c.id));
        const rows = (response.items ?? []).map(toSegmentContactRow);
        rows.forEach(row => this.knownContacts.set(row.id, row));

        this.availableContactOptions = [
          ...this.availableContactOptions,
          ...rows.filter(row => !newIds.has(row.id)),
        ];
        this.cdr.markForCheck();
      },
    });
  }

  private appendAssigned(response: ListResponseDto<CrmSegmentContactDto>): void {
    if (this.removeAll) {
      return;
    }

    const rows = (response.items ?? [])
      .map(toSegmentContactRow)
      .filter(row => !this.removedAssignedIds.has(row.id));
    this.alreadyAssigned = [...this.alreadyAssigned, ...rows];
    this.cdr.markForCheck();
  }

  /**
   * Runs one page request for a paged list: advances its offset and total, marks it loading meanwhile, and
   * drops the response of a request the list has been reset past (a newer search, a reopened dialog).
   */
  private fetchPage(
    state: PagedContactsState,
    request: () => Observable<ListResponseDto<CrmSegmentContactDto>>,
  ): Observable<ListResponseDto<CrmSegmentContactDto>> {
    const requestId = ++state.requestId;
    const isCurrent = () =>
      requestId === state.requestId && (state === this.assignedState || state === this.pickerState);

    state.loading = true;
    this.cdr.markForCheck();

    return request().pipe(
      filter(() => isCurrent()),
      tap(response => {
        state.fetched += response.items?.length ?? 0;
        state.total = response.totalCount ?? state.fetched;
      }),
      finalize(() => {
        if (isCurrent()) {
          state.loading = false;
          this.cdr.markForCheck();
        }
      }),
    );
  }

  private emptyPagedState(requestId = 0): PagedContactsState {
    return { fetched: 0, total: null, loading: false, requestId };
  }

  private resetState(): void {
    this.assignedState = this.emptyPagedState(this.assignedState.requestId);
    this.pickerState = this.emptyPagedState(this.pickerState.requestId);
    this.alreadyAssigned = [];
    this.newContacts = [];
    this.availableContactOptions = [];
    this.selectedContactIds = [];
    this.removedAssignedIds.clear();
    this.removeAll = false;
    this.knownContacts.clear();
    this.searchTerm = '';
    this.isAssignedExpanded = false;
    this.saving = false;
  }
}
